import Section from "../components/Section";
import { motion } from "framer-motion";

const container = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45 } } };

export default function AboutCare() {
  return (
    <>
      {/* Goals */}
      <Section eyebrow="Care Goals" title="What does good care aim to do?">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid sm:grid-cols-3 gap-4"
        >
          <StatCard variants={item} number="Maintain Hb" label="Reduce anaemia symptoms" />
          <StatCard variants={item} number="Limit Iron" label="Prevent organ damage" />
          <StatCard variants={item} number="Quality of Life" label="Growth & daily activities" />
        </motion.div>
      </Section>

      {/* Pillars */}
      <Section eyebrow="Care & Treatment" title="Core components of management">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          <IconCard variants={item} icon="fa-syringe" title="Blood Transfusions" img="/transfusion.jpeg">
            Typically every 3–4 weeks to maintain haemoglobin.
          </IconCard>
          <IconCard variants={item} icon="fa-pills" title="Iron Chelation" img="/chelation.jpg">
            Medicines that remove excess iron from the body.
          </IconCard>
          <IconCard variants={item} icon="fa-notes-medical" title="Monitoring & Vaccines" img="/monitor.webp">
            Regular tests, infection prevention, doctor follow-ups.
          </IconCard>
          <IconCard variants={item} icon="fa-apple-whole" title="Nutrition & Lifestyle" img="/nutrition.jpg">
            Balanced diet, folic acid if prescribed, routine.
          </IconCard>
        </motion.div>

        <Callout type="ok">
          There isn’t a permanent cure for most patients, but thalassemia can be managed well with proper medical care.
        </Callout>
      </Section>

      {/* Advanced + Team */}
      <Section eyebrow="Advanced" title="Is there a cure?">
        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-xl border bg-white p-6 shadow-sm"
          >
            <div className="flex items-center gap-3 mb-3">
              <i className="fa-solid fa-briefcase-medical text-rose-600" />
              <h3 className="font-semibold text-slate-900">Bone Marrow Transplant (BMT)</h3>
            </div>
            <p className="text-slate-600">
              BMT can be curative in select cases with a suitable donor. It has risks and costs; decisions are made with specialists.
            </p>
          </motion.div>

          <motion.div
            variants={item}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="rounded-xl border bg-white p-6 shadow-sm"
          >
            <h4 className="font-semibold text-slate-900 mb-3">Your Care Team</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <Chip icon="fa-user-doctor" text="Haematologist" />
              <Chip icon="fa-user-nurse" text="Paediatrician" />
              <Chip icon="fa-apple-whole" text="Dietician" />
              <Chip icon="fa-people-group" text="Counsellor / Support" />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Do/Don't */}
      <Section eyebrow="Daily Living" title="Helpful habits">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid md:grid-cols-2 gap-6"
        >
          <ListCard variants={item} title="Do">
            <ListItem>Keep transfusion schedules and follow-ups.</ListItem>
            <ListItem>Take doctor-prescribed chelation/folic acid.</ListItem>
            <ListItem>Practice infection precautions and vaccines.</ListItem>
          </ListCard>
          <ListCard variants={item} title="Don’t">
            <ListItem>Take iron supplements unless advised by a doctor.</ListItem>
            <ListItem>Skip transfusions or self-medicate.</ListItem>
            <ListItem>Rely on home remedies for serious symptoms.</ListItem>
          </ListCard>
        </motion.div>
      </Section>

      {/* CTA */}
      <Section eyebrow="Get Help" title="Need guidance or services?">
        <div className="flex flex-wrap gap-3">
          <a href="/resources" className="px-4 py-2 rounded bg-rose-600 text-white">Explore Resources</a>
          <a href="/support" className="px-4 py-2 rounded border">Find Support</a>
        </div>
      </Section>
    </>
  );
}

function StatCard({ number, label, variants }) {
  return (
    <motion.div variants={variants} whileHover={{ y: -3 }} className="rounded-xl border bg-white p-5 text-center shadow-sm">
      <div className="text-slate-900 font-semibold">{number}</div>
      <div className="text-slate-600 text-sm">{label}</div>
    </motion.div>
  );
}
function IconCard({ icon, title, children, img, variants }) {
  return (
    <motion.div
      variants={variants}
      whileHover={{ y: -3 }}
      className="rounded-xl border p-5 bg-white shadow-sm flex flex-col min-h-[300px]"
    >
      {img && (
        <div className="h-28 w-full overflow-hidden rounded-lg border bg-slate-50 mb-3">
          <img src={img} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="flex items-center gap-3 mb-2">
        <i className={`fa-solid ${icon} text-rose-600`} />
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-slate-600 mt-2 text-sm flex-grow">{children}</p>
    </motion.div>
  );
}

function Chip({ icon, text }) {
  return (
    <div className="flex items-center gap-2 text-sm px-3 py-2 rounded border bg-slate-50">
      <i className={`fa-solid ${icon}`} />
      <span className="text-slate-700">{text}</span>
    </div>
  );
}
function ListCard({ title, children, variants }) {
  return (
    <motion.div variants={variants} className="rounded-xl border bg-white p-6 shadow-sm">
      <h3 className="font-semibold text-slate-900 mb-3">{title}</h3>
      <ul className="space-y-2">{children}</ul>
    </motion.div>
  );
}
function ListItem({ children }) {
  return (
    <li className="flex items-start gap-3">
      <i className="fa-solid fa-circle-check text-emerald-600 mt-1" />
      <span className="text-slate-700">{children}</span>
    </li>
  );
}
function Callout({ type = "ok", children }) {
  const map = {
    ok: "border-emerald-200 bg-emerald-50 text-emerald-800",
    warn: "border-amber-200 bg-amber-50 text-amber-800",
    danger: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return <div className={`mt-6 p-4 rounded-xl border-l-4 ${map[type]}`}>{children}</div>;
}
